import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';

// ---------------------------------------------------------------------------
// Departments API — mirrors backend/src/routes/departments.ts (CLE-260).
// Departments are company-level org units: people belong to one (optional)
// and tasks can be tagged with one (defaulting from the assignee's).
// ---------------------------------------------------------------------------

export interface Department {
  id: string;
  name: string;
  color: string;
  peopleCount: number;
  taskCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDepartmentInput {
  name: string;
  color?: string;
}

export interface UpdateDepartmentInput {
  name?: string;
  color?: string;
}

export interface DeleteDepartmentResult {
  deleted: boolean;
  id: string;
  detachedPeople: number;
  detachedTasks: number;
}

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** GET /api/departments — all departments, ordered by name. */
export async function listDepartments(): Promise<Department[]> {
  const res = await apiFetch<Envelope<Department[]>>('/api/departments', {
    headers: authHeaders(),
  });
  return res.data;
}

/** POST /api/departments — create a department (settings.canEditDepartments). */
export async function createDepartment(input: CreateDepartmentInput): Promise<Department> {
  const res = await apiFetch<Envelope<Department>>('/api/departments', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

/** PATCH /api/departments/:id — rename / recolor a department. */
export async function updateDepartment(
  id: string,
  input: UpdateDepartmentInput,
): Promise<Department> {
  const res = await apiFetch<Envelope<Department>>(`/api/departments/${id}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

/**
 * DELETE /api/departments/:id — delete a department. People and tasks that
 * referenced it are detached (departmentId set to null), counts returned.
 */
export async function deleteDepartment(id: string): Promise<DeleteDepartmentResult> {
  const res = await apiFetch<Envelope<DeleteDepartmentResult>>(`/api/departments/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  return res.data;
}
